import { shouldNeverHappen, switchOn } from '@moojo/misc-constructs'

export type DurationUnit = 'millis' | 'seconds' | 'minutes' | 'hours' | 'days'

/**
 * An immutable length of time, in milliseconds granularity
 */
// #TD(imaman): zod support
export class Duration {
  constructor(private readonly amountMillis: number) {
    if (!Number.isFinite(amountMillis)) {
      throw new Error(`Not a finite number (${amountMillis})`)
    }
    if (!Number.isSafeInteger(amountMillis)) {
      throw new Error(`Not a (safe) int (${amountMillis})`)
    }
  }

  private exactUnrounded(targetUnit: DurationUnit) {
    return this.amountMillis / ratioToMillis(targetUnit)
  }

  exact(targetUnit: DurationUnit, precision: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 = 10) {
    const factor = Math.pow(10, precision)
    const rounded = Math.round(this.exactUnrounded(targetUnit) * factor)
    return rounded / factor
  }

  private abs() {
    return this.isNegative() ? durationOf(0).minus(this) : this
  }

  whole(targetUnit: DurationUnit): number {
    const n = Math.floor(this.abs().exactUnrounded(targetUnit))
    return this.isNegative() ? -n : n
  }

  private sign() {
    return this.isNegative() ? '-' : ''
  }

  toString() {
    return this.format()
  }

  isZero() {
    return this.amountMillis === 0
  }

  isNegative() {
    return this.amountMillis < 0
  }

  negate() {
    return new Duration(-this.amountMillis)
  }

  format() {
    if (this.isZero()) {
      return `0${switchOn('millis', letters)}`
    }
    // TODO(imaman): can we compute it hand-free (to avoid manually updating it when a new unit is added)
    const units: DurationUnit[] = ['days', 'hours', 'minutes', 'seconds', 'millis']
    const arr = []
    let d: Duration = this.abs()
    for (const u of units) {
      const n = d.whole(u)
      if (n > 0) {
        arr.push(`${n}${switchOn(u, letters)}`)
      }
      d = d.minus(durationOf(n, u))
    }

    return `${this.sign()}${arr.join('')}`
  }

  plus(rhs: Duration): Duration {
    return new Duration(this.amountMillis + rhs.amountMillis)
  }
  minus(rhs: Duration): Duration {
    return new Duration(this.amountMillis - rhs.amountMillis)
  }

  compare(that: Duration): -1 | 0 | 1 {
    const diff = this.minus(that)
    return diff.isZero() ? 0 : diff.isNegative() ? -1 : 1
  }

  public is(operator: '<' | '<=' | '==' | '>=' | '>', other: Duration) {
    const c = this.compare(other)
    return switchOn(operator, {
      '<': () => c < 0,
      '<=': () => c <= 0,
      '==': () => c == 0,
      '>=': () => c >= 0,
      '>': () => c > 0,
    })
  }
}

const letters: Record<DurationUnit, () => string> = {
  millis: () => 'ms',
  seconds: () => 's',
  minutes: () => 'm',
  hours: () => 'h',
  days: () => 'd',
}

function ratioToMillis(unit: DurationUnit) {
  if (unit === 'millis') {
    return 1
  }
  if (unit === 'seconds') {
    return 1000
  }
  if (unit === 'minutes') {
    return 1000 * 60
  }
  if (unit === 'hours') {
    return 1000 * 60 * 60
  }

  if (unit === 'days') {
    return 1000 * 60 * 60 * 24
  }

  shouldNeverHappen(unit)
}

export function durationOf(amount: 0): Duration
export function durationOf(amount: number, unit: DurationUnit): Duration
export function durationOf(...args: [amount: number, unit: DurationUnit] | [amount: 0]) {
  if (args.length === 1) {
    return new Duration(args[0])
  }

  if (args.length === 2) {
    const [amount, unit] = args
    return new Duration(ratioToMillis(unit) * amount)
  }

  shouldNeverHappen(args)
}
